/**
 * Post Affiliate Pro — vigmd account (click + sale helpers).
 * Script tag lives in public/index.html (id pap_x2s6df8d).
 */

export const PAP_SCRIPT_ID = "pap_x2s6df8d";
export const PAP_SCRIPT_SRC = "https://vigmd.postaffiliatepro.com/scripts/55m6m9j68";
export const PAP_ACCOUNT_ID = "default1";

const STORAGE_AID = "pap_pending_a_aid";
const STORAGE_BID = "pap_pending_a_bid";

function papDebugEnabled() {
  try {
    if (typeof window === "undefined") return false;
    if (window.sessionStorage?.getItem("PAP_DEBUG") === "1") return true;
    return new URLSearchParams(window.location.search).get("pap_debug") === "1";
  } catch {
    return false;
  }
}

function papLog(...args) {
  if (papDebugEnabled()) console.info("[PAP]", ...args);
}

function storeAffiliateFromParams(params) {
  const aid = params.get("a_aid");
  const bid = params.get("a_bid");
  try {
    if (aid) sessionStorage.setItem(STORAGE_AID, aid);
    if (bid) sessionStorage.setItem(STORAGE_BID, bid);
  } catch {
    /* private mode */
  }
}

export function getStoredPapAffiliateIds() {
  try {
    return {
      a_aid: sessionStorage.getItem(STORAGE_AID),
      a_bid: sessionStorage.getItem(STORAGE_BID),
    };
  } catch {
    return { a_aid: null, a_bid: null };
  }
}

/**
 * 1) #nimavat → #a_aid=nimavat
 * 2) Promote #a_aid / #a_bid into the query string (PAP click requests use getParams from the page URL).
 * 3) Remember affiliate in sessionStorage for sale attribution if cookies are blocked.
 */
export function preparePapAffiliateInBrowserUrl() {
  if (typeof window === "undefined") return;

  const { pathname, search, hash } = window.location;
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);

  let fragRaw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!fragRaw) {
    if (params.get("a_aid") || params.get("a_bid")) {
      storeAffiliateFromParams(params);
    }
    return;
  }

  let work = decodeURIComponent(fragRaw);
  if (!work.includes("=")) {
    const token = work.trim();
    if (token && !/[#?&/]/.test(token)) {
      work = `a_aid=${encodeURIComponent(token)}`;
    }
  }

  const fromHash = new URLSearchParams(work);
  const aidH = fromHash.get("a_aid");
  const bidH = fromHash.get("a_bid");

  let changed = false;
  if (aidH && !params.get("a_aid")) {
    params.set("a_aid", aidH);
    changed = true;
  }
  if (bidH && !params.get("a_bid")) {
    params.set("a_bid", bidH);
    changed = true;
  }

  const newHashParts = new URLSearchParams();
  fromHash.forEach((v, k) => {
    if (k !== "a_aid" && k !== "a_bid") newHashParts.set(k, v);
  });
  const rest = newHashParts.toString();
  const newHash = rest ? `#${rest}` : "";

  const qs = params.toString();
  const nextUrl = `${pathname}${qs ? `?${qs}` : ""}${newHash}`;

  if (changed || nextUrl !== pathname + search + hash) {
    window.history.replaceState(window.history.state, "", nextUrl);
    papLog("URL prepared for PAP:", nextUrl);
  }

  storeAffiliateFromParams(new URLSearchParams(qs));
}

/** @deprecated use preparePapAffiliateInBrowserUrl */
export function normalizeAffiliateUrlHash() {
  preparePapAffiliateInBrowserUrl();
}

function applyPapCookieDomain(tracker) {
  const h = window.location.hostname;
  if (!h || h === "localhost" || h === "127.0.0.1" || h.endsWith(".local")) return;
  if (!/(^|\.)vigmd\.com$/i.test(h)) return;
  try {
    tracker.setCookieDomain(".vigmd.com");
    papLog("setCookieDomain(.vigmd.com)");
  } catch (e) {
    papLog("setCookieDomain skipped", e);
  }
}

function loadPostAffScript() {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      resolve();
      return;
    }
    let el = document.getElementById(PAP_SCRIPT_ID);
    if (!el) {
      el = document.createElement("script");
      el.type = "text/javascript";
      el.id = PAP_SCRIPT_ID;
      el.src = PAP_SCRIPT_SRC;
      el.referrerPolicy = "no-referrer-when-downgrade";
      el.onload = () => resolve();
      el.onerror = () => reject(new Error("PostAffiliatePro script failed to load"));
      document.head.appendChild(el);
      return;
    }
    // Script tag already in DOM (index.html). "load" may have fired already — never wait forever.
    resolve();
  });
}

export function waitForPostAffTracker(maxWaitMs = 20000) {
  return new Promise((resolve) => {
    const start = Date.now();
    const poll = () => {
      if (window.PostAffTracker) {
        resolve(window.PostAffTracker);
        return;
      }
      if (Date.now() - start >= maxWaitMs) {
        papLog("PostAffTracker not available after", maxWaitMs, "ms");
        resolve(null);
        return;
      }
      window.setTimeout(poll, 50);
    };
    poll();
  });
}

/** Click / referral tracking — call on each route (after preparePapAffiliateInBrowserUrl). */
export async function trackPostAffiliateClick() {
  try {
    await loadPostAffScript();
    const tracker = await waitForPostAffTracker();
    if (!tracker) return;
    applyPapCookieDomain(tracker);
    tracker.setAccountId(PAP_ACCOUNT_ID);
    tracker.track();
    papLog("track() fired", window.location.href);
  } catch (e) {
    papLog("trackPostAffiliateClick error", e);
  }
}

function saleDedupeKey(orderIdsNorm) {
  return `pap_sale_reg_${orderIdsNorm.map(String).join("_")}`;
}

/**
 * Sale / conversion registration. Mirrors checkout → thank-you state shape from Checkout2.
 */
export function registerPostAffiliateSale({
  orderId,
  orderIds = [],
  productTotal,
  productId,
  productIds = [],
  productSku,
  productSkus = [],
  productPrices = [],
}) {
  const orderIdsNorm = (orderIds && orderIds.length ? orderIds : [orderId]).filter(Boolean);
  const hasOrderData = orderIdsNorm.length > 0;

  const skusOrIds =
    productSkus && productSkus.length
      ? productSkus
      : productSku
        ? [productSku]
        : productIds && productIds.length
          ? productIds
          : productId
            ? [productId]
            : [];

  const totalNum =
    typeof productTotal === "number"
      ? productTotal
      : parseFloat(String(productTotal || "").replace(/[^0-9.-]/g, ""));

  const hasProductData = skusOrIds.length > 0;
  const hasTotal = Number.isFinite(totalNum) && totalNum > 0;

  if (!hasOrderData || !hasProductData || !hasTotal || !window.PostAffTracker) {
    papLog("register skipped", {
      hasOrderData,
      hasProductData,
      hasTotal,
      hasTracker: !!window.PostAffTracker,
    });
    return;
  }

  try {
    if (sessionStorage.getItem(saleDedupeKey(orderIdsNorm))) {
      papLog("register skipped (dedupe)", orderIdsNorm);
      return;
    }
  } catch {
    /* continue */
  }

  const { a_aid: forcedAid, a_bid: forcedBid } = getStoredPapAffiliateIds();

  window.PostAffTracker.setAccountId(PAP_ACCOUNT_ID);
  applyPapCookieDomain(window.PostAffTracker);

  const applyForcedIds = (sale) => {
    if (forcedAid && typeof sale.setAffiliateID === "function") {
      try {
        sale.setAffiliateID(forcedAid);
      } catch (e) {
        papLog("setAffiliateID", e);
      }
    }
    if (forcedBid && typeof sale.setBannerID === "function") {
      try {
        sale.setBannerID(forcedBid);
      } catch (e) {
        papLog("setBannerID", e);
      }
    }
  };

  if (skusOrIds.length > 1) {
    skusOrIds.forEach((skuOrId, index) => {
      const sale = window.PostAffTracker.createSale();
      applyForcedIds(sale);
      const productPrice = productPrices[index]
        ? productPrices[index]
        : (totalNum / skusOrIds.length).toFixed(2);
      sale.setTotalCost(String(productPrice));
      const saleOrderId = orderIdsNorm[index]
        ? orderIdsNorm[index]
        : `${orderIdsNorm[0] || orderId}(${index + 1})`;
      sale.setOrderID(String(saleOrderId));
      sale.setProductID(String(skuOrId));
    });
  } else {
    const sale = window.PostAffTracker.createSale();
    applyForcedIds(sale);
    sale.setTotalCost(String(totalNum));
    sale.setOrderID(String(orderIdsNorm[0] || orderId));
    sale.setProductID(String(skusOrIds[0]));
  }

  window.PostAffTracker.register();
  papLog("register() sent", { orderIdsNorm, totalNum, skusOrIds, forcedAid });

  try {
    sessionStorage.setItem(saleDedupeKey(orderIdsNorm), "1");
  } catch {
    /* ignore */
  }
}

/** Loads PAP (if needed), waits for tracker, then registers the sale. */
export async function registerPostAffiliateSaleAsync(payload) {
  try {
    await loadPostAffScript();
    await waitForPostAffTracker();
    registerPostAffiliateSale(payload);
  } catch (e) {
    papLog("registerPostAffiliateSaleAsync error", e);
  }
}
